import { SoundProcessorOptionsChangedEvent } from '@yandex-video-platform/goloom-sdk';
import { call, put, select } from 'typed-redux-saga';

import { NoiseCancellationType, VirtualBackgroundType } from '../../../../../../common/types/settings';
import { selectIsTestplane } from '../../../../shared/features/flags/selectors';
import { hideNotification, NotificationType, notify } from '../../../../shared/features/notifications/ps-notifications';
import { selectIsNoiseCancellationEnabled, selectNoiseCancellationType } from '../../../../shared/features/settings';
import { setNoiseCancellationType } from '../../../../shared/features/settings/sagas';
import { getUserSelector } from '../../../../shared/features/user';
import { i18n } from '../../../../shared/helpers/i18n';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { NotificationId } from '../../types';

export interface NoiseCancellationTypeChangedEvent {
    type: NoiseCancellationType;
    reason: SoundProcessorOptionsChangedEvent['reason'];
}

export interface VirtualBackgroundTypeChangedEvent {
    type: VirtualBackgroundType;
}

const NOTIFICATION_TIMEOUT = 5000;

const logger = createLogger('goloom:noise-cancellation');

export function* onSoundProcessorOptionsChangedSaga({ type, reason }: NoiseCancellationTypeChangedEvent) {
    logger.event(`Noise cancellation changed. Source: SDK. Value: ${type}. Reason: ${reason}`, {
        additional: {
            feature: 'noise_cancellation',
            source: 'SDK',
            value: type,
            reason,
        },
    });

    // Выходим из саги и не показываем уведомление, если:
    // - страница открывается в гермионе, так как это создаст флапы
    // - для пользователя не включена фича
    // - если событие пришло из-за переключения режима самим пользователем (reason: 'request')
    const isTestplane = yield* select(selectIsTestplane);
    const isNoiseCancellationEnabled = yield* select(selectIsNoiseCancellationEnabled);
    const user = yield* select(getUserSelector);
    const currentNoiseCancellationMode = yield* select(selectNoiseCancellationType);

    if (isTestplane || !isNoiseCancellationEnabled || reason === 'request') {
        return;
    }

    const isCurrentTypeAuto = currentNoiseCancellationMode === NoiseCancellationType.AUTO;
    const isCurrentTypeHigh = currentNoiseCancellationMode === NoiseCancellationType.HIGH;
    const isTemporarily = Boolean(user?.isYandexSsoUser || user?.isYandexTeamUser) || isCurrentTypeHigh;

    if (!isCurrentTypeAuto) {
        yield* call(setNoiseCancellationType, type, isTemporarily);
    }

    countMetrika('meeting_screen', 'noise_cancellation', 'sdk', type);

    const messsageKey = (() => {
        switch (type) {
            case NoiseCancellationType.DISABLED:
                return 'notification-noise-cancellation-disabled';
            case NoiseCancellationType.AUTO:
                return 'notification-noise-cancellation-enabled';
            case NoiseCancellationType.HIGH:
                return 'notification-noise-cancellation-high';
            case NoiseCancellationType.LOW:
                return 'notification-noise-cancellation-low';
        }
    })();

    yield* put(hideNotification(NotificationId.NOISE_CANCELLATION_STATUS));
    yield* put(
        notify({
            id: NotificationId.NOISE_CANCELLATION_STATUS,
            type: NotificationType.INFO,
            timeout: NOTIFICATION_TIMEOUT,
            text: i18n('statuses', messsageKey),
            icon: 'waves',
        })
    );
}
